import { Component, Inject } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { MatChipInputEvent } from '@angular/material/chips';
import { COMMA, ENTER } from '@angular/cdk/keycodes';
import { faPlus, faMinus } from '@fortawesome/free-solid-svg-icons';

// in add-item-dialog.component.ts
export interface ExtraData {
  extraId?: number;
  extraName: string;
  extraPrice: number | null;
}

export interface OptionGroupData {
  groupName: string;
  groupDescription: string;
  minSelect: number;
  maxSelect: number;
  extras: ExtraData[];
}

export interface AvailableGroup {
  optionId: number;
  groupName: string;
}

export interface NewItemData {
  name: string;
  description: string;
  price: number | null;
  optionGroups?: OptionGroupData[];
  categoryName: string;
  availableGroups?: AvailableGroup[];
  selectedGroupIds?: number[];
  attachedGroups?: OptionGroupData[];
}

@Component({
  selector: 'app-add-item-dialog',
  standalone: false,
  templateUrl: './add-item-dialog.component.html',
  styleUrl: './add-item-dialog.component.scss',
})
export class AddItemDialogComponent {
  availableGroups: AvailableGroup[] = [];
  selectedGroupIds: number[] = [];

  attachedGroups: OptionGroupData[] = [];

  // for the chip input
  readonly separatorKeysCodes = [ENTER, COMMA] as const;

  showExtras = false;

  // FAICONS
  faPlus = faPlus;
  faMinus = faMinus;
  // working copy of groups
  optionGroups: OptionGroupData[] = [];

  constructor(
    private dialogRef: MatDialogRef<AddItemDialogComponent, NewItemData>,
    @Inject(MAT_DIALOG_DATA) public data: NewItemData
  ) {
    this.availableGroups = data.availableGroups ?? [];
    this.selectedGroupIds = [...(data.selectedGroupIds ?? [])];
    this.attachedGroups = data.attachedGroups ?? [];
  }

  /** Add a new blank group */
  addGroup() {
    this.optionGroups.push({
      groupName: '',
      groupDescription: '',
      minSelect: 0,
      maxSelect: 1,
      extras: [],
    });
  }

  /** Remove a group at idx */
  removeGroup(idx: number) {
    this.optionGroups.splice(idx, 1);
  }

  /** Add an extra to group idx */
  addExtra(
    idx: number,
    nameInput: HTMLInputElement,
    priceInput: HTMLInputElement
  ) {
    const name = nameInput.value.trim();
    const price = parseFloat(priceInput.value);
    if (!name) return;

    const grp = this.optionGroups[idx];
    grp.extras.push({
      extraName: name,
      extraPrice: isNaN(price) ? null : price,
    });

    // auto-bump maxSelect if it was blank or below the new length
    if (!grp.maxSelect || grp.maxSelect < grp.extras.length) {
      grp.maxSelect = grp.extras.length;
    }

    nameInput.value = '';
    priceInput.value = '';
  }

  /** Remove extra eidx from group idx */
  removeExtra(idx: number, eidx: number) {
    const grp = this.optionGroups[idx];
    grp.extras.splice(eidx, 1);

    // if maxSelect now exceeds the new length, clamp it down
    if (grp.maxSelect > grp.extras.length) {
      grp.maxSelect = grp.extras.length;
    }
  }

  save(): void {
    this.data.selectedGroupIds = this.selectedGroupIds;
    this.dialogRef.close(this.data);
  }

  cancel(): void {
    this.dialogRef.close();
  }
}
